import { AssetHierarchyLevelsService, type AssetHierarchyLevelDto } from '@/shared/services/assetHierarchyLevels';
import { getActivePinia } from 'pinia';
import { useProjectStore } from '@/shared/stores/project';

export interface RootInfo {
  rootPosition: number;
  rootLevel: AssetHierarchyLevelDto | null;
  hasLevels: boolean;
  /** All hierarchy level positions sorted ascending, used to map position → sorted 0-based index */
  sortedPositions: number[];
}

const rootInfoCache = new Map<string, RootInfo>();
const inFlightRequests = new Map<string, Promise<RootInfo>>();
const inFlightIsRefresh = new Map<string, boolean>();

function resolveProjectKey(projectId: string | null | undefined): string {
  return projectId ?? 'default';
}

function getActiveProjectId(fallbackProjectId?: string | null): string | null {
  if (fallbackProjectId !== undefined) {
    return fallbackProjectId;
  }

  if (!getActivePinia()) {
    return null;
  }

  const projectStore = useProjectStore();
  return projectStore.selectedProjectId ?? null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function computeRootInfo(levels: AssetHierarchyLevelDto[]): RootInfo {
  const levelsWithPositions = levels
    .map((level) => ({ level, position: level.position }))
    .filter((x): x is { level: AssetHierarchyLevelDto; position: number } => isFiniteNumber(x.position));

  if (levelsWithPositions.length === 0) {
    return {
      rootPosition: 1,
      rootLevel: null,
      hasLevels: false,
      sortedPositions: [],
    };
  }

  levelsWithPositions.sort((a, b) => a.position - b.position);
  const root = levelsWithPositions[0];

  return {
    rootPosition: root.position,
    rootLevel: root.level,
    hasLevels: true,
    sortedPositions: levelsWithPositions.map((x) => x.position),
  };
}

async function fetchRootInfo(): Promise<RootInfo> {
  const allLevels = await AssetHierarchyLevelsService.getAll();
  return computeRootInfo(allLevels);
}

export interface UseAssetHierarchyRootPositionReturn {
  getRootInfo: (options?: { forceRefresh?: boolean; projectId?: string | null }) => Promise<RootInfo>;
  refreshRootInfo: (projectId?: string | null) => Promise<RootInfo>;
  clearRootInfoCache: (projectId?: string | null) => void;
}

export function useAssetHierarchyRootPosition(): UseAssetHierarchyRootPositionReturn {
  async function getRootInfo(options?: { forceRefresh?: boolean; projectId?: string | null }): Promise<RootInfo> {
    const projectId = getActiveProjectId(options?.projectId);
    const projectKey = resolveProjectKey(projectId);
    const forceRefresh = options?.forceRefresh ?? false;

    if (!forceRefresh) {
      const cached = rootInfoCache.get(projectKey);
      if (cached) {
        return cached;
      }
    }

    const inFlight = inFlightRequests.get(projectKey) ?? null;
    const inFlightRefresh = inFlightIsRefresh.get(projectKey) ?? false;

    if (inFlight) {
      if (forceRefresh && !inFlightRefresh) {
        // Replace in-flight non-refresh request with a refresh request.
        inFlightIsRefresh.set(projectKey, true);
        const refreshPromise = fetchRootInfo()
          .then((info) => {
            rootInfoCache.set(projectKey, info);
            return info;
          })
          .finally(() => {
            inFlightRequests.delete(projectKey);
            inFlightIsRefresh.delete(projectKey);
          });
        inFlightRequests.set(projectKey, refreshPromise);
        return refreshPromise;
      }

      return inFlight;
    }

    inFlightIsRefresh.set(projectKey, forceRefresh);
    const nextRequest = fetchRootInfo()
      .then((info) => {
        rootInfoCache.set(projectKey, info);
        return info;
      })
      .finally(() => {
        inFlightRequests.delete(projectKey);
        inFlightIsRefresh.delete(projectKey);
      });

    inFlightRequests.set(projectKey, nextRequest);
    return nextRequest;
  }

  async function refreshRootInfo(projectId?: string | null): Promise<RootInfo> {
    const resolvedProjectId = getActiveProjectId(projectId);
    const projectKey = resolveProjectKey(resolvedProjectId);
    rootInfoCache.delete(projectKey);
    return getRootInfo({ forceRefresh: true, projectId: resolvedProjectId });
  }

  function clearRootInfoCache(projectId?: string | null): void {
    if (projectId !== undefined) {
      const projectKey = resolveProjectKey(projectId);
      rootInfoCache.delete(projectKey);
      inFlightRequests.delete(projectKey);
      inFlightIsRefresh.delete(projectKey);
      return;
    }

    rootInfoCache.clear();
    inFlightRequests.clear();
    inFlightIsRefresh.clear();
  }

  return {
    getRootInfo,
    refreshRootInfo,
    clearRootInfoCache,
  };
}
